/** Team world clocks for the Home page — digital, 24-hour, one per office. Times come from Intl with IANA zones, so
 *  British Summer Time, Sydney daylight saving (and any future rule change) are handled by the browser's tz database,
 *  not by us. Dean, 3 Nov 2026. */
import { useEffect, useMemo, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { Flag } from "@/components/Flag";

export const CLOCKS = [
  { key: "london", flag: "UK", city: "London",  country: "UK",          tz: "Europe/London",    locale: "en-GB", zoneNames: { "UTC+0": "GMT", "UTC+1": "BST" } },
  { key: "sydney", flag: "AU", city: "Sydney",  country: "Australia",   tz: "Australia/Sydney", locale: "en-AU", zoneNames: { "UTC+10": "AEST", "UTC+11": "AEDT" } },
  { key: "indore", flag: "IN", city: "Indore",  country: "India",       tz: "Asia/Kolkata",     locale: "en-IN", zoneNames: { "UTC+5:30": "IST" } },
  { key: "manila", flag: "PH", city: "Manila",  country: "Philippines", tz: "Asia/Manila",      locale: "en-PH", zoneNames: { "UTC+8": "PHT" } },
];

const fmtCache = {};
const fmt = (tz, opts, locale = "en-GB") => {
  const k = locale + tz + JSON.stringify(opts);
  return (fmtCache[k] ||= new Intl.DateTimeFormat(locale, { timeZone: tz, ...opts }));
};
const tzPart = (now, tz, style, locale) => (fmt(tz, { timeZoneName: style, hour: "2-digit" }, locale).formatToParts(now).find((p) => p.type === "timeZoneName") || {}).value || "";

export function clockParts(now, { tz, locale = "en-GB", zoneNames = {} }) {
  const time = fmt(tz, { hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).format(now);
  const day = fmt(tz, { weekday: "short", day: "2-digit", month: "short" }).format(now);
  const offset = tzPart(now, tz, "shortOffset").replace(/^GMT$/, "UTC+0").replace(/^GMT/, "UTC");
  // BST / GMT, AEST / AEDT, IST — the DST state spelled out. Some browsers only know "GMT+1", so fall back to the
  // name for the CURRENT offset (which is itself DST-aware) rather than a fixed label.
  let zone = tzPart(now, tz, "short", locale);
  if (/^GMT[+-]?/.test(zone) || !zone) zone = zoneNames[offset] || "";
  const hour = parseInt(time.slice(0, 2), 10);
  // "tomorrow" / "yesterday" relative to the viewer's own calendar day
  const localDay = fmt(Intl.DateTimeFormat().resolvedOptions().timeZone, { day: "2-digit" }).format(now);
  const theirDay = fmt(tz, { day: "2-digit" }).format(now);
  const dayDelta = theirDay === localDay ? "" : (parseInt(theirDay, 10) - parseInt(localDay, 10) + 31) % 31 === 1 ? "tomorrow" : "yesterday";
  return { time, day, zone, offset, night: hour < 7 || hour >= 20, dayDelta };
}

export function WorldClocks({ compact = false }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const align = 1000 - (Date.now() % 1000);
    let id;
    const t = setTimeout(() => { setNow(new Date()); id = setInterval(() => setNow(new Date()), 1000); }, align);
    return () => { clearTimeout(t); clearInterval(id); };
  }, []);
  const parts = useMemo(() => CLOCKS.map((c) => ({ ...c, ...clockParts(now, c) })), [now]);

  if (compact) {
    return (
      <div className="grid grid-cols-4 gap-1.5" data-testid="home-world-clocks">
        {parts.map((c) => (
          <div key={c.key} className={`rounded-lg border px-2 py-1.5 text-center ${c.night ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900"}`} data-testid={`home-clock-${c.key}`}>
            <div className="flex items-center justify-center gap-1 text-[10px] font-medium truncate"><Flag code={c.flag} size={11} />{c.city}</div>
            <p className="font-mono text-base font-semibold tabular-nums leading-tight mt-0.5" data-testid={`home-clock-time-${c.key}`}>{c.time.slice(0, 5)}</p>
            <p className={`text-[9px] leading-tight ${c.night ? "text-slate-400" : "text-slate-500"}`} data-testid={`home-clock-meta-${c.key}`}>{c.zone || c.offset}{c.dayDelta && ` · ${c.dayDelta === "tomorrow" ? "+1d" : "−1d"}`}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-world-clocks">
      {parts.map((c) => (
        <div key={c.key} className={`relative overflow-hidden rounded-xl border px-4 py-3 transition-colors ${c.night ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900"}`} data-testid={`home-clock-${c.key}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Flag code={c.flag} size={18} />
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">{c.city}</p>
                <p className={`text-[11px] leading-tight truncate ${c.night ? "text-slate-400" : "text-slate-500"}`}>{c.country}</p>
              </div>
            </div>
            {c.night ? <Moon className="w-4 h-4 text-indigo-300 shrink-0" /> : <Sun className="w-4 h-4 text-amber-500 shrink-0" />}
          </div>
          <p className="mt-2 font-mono text-3xl font-semibold tabular-nums tracking-tight leading-none" data-testid={`home-clock-time-${c.key}`}>{c.time}</p>
          <p className={`mt-1.5 text-[11px] ${c.night ? "text-slate-400" : "text-slate-500"}`} data-testid={`home-clock-meta-${c.key}`}>
            {c.day}{c.dayDelta && <span className={`ml-1 font-medium ${c.night ? "text-indigo-300" : "text-sky-700"}`}>· {c.dayDelta}</span>}
            <span className="mx-1.5 opacity-50">|</span>{c.zone && <span className="font-medium">{c.zone} </span>}<span className="opacity-70">{c.offset}</span>
          </p>
        </div>
      ))}
    </div>
  );
}
